import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../services/AxiosInstance";

// GET /api/house-details/:home_id
export const fetchHouseDetailsThunk = createAsyncThunk(
  "houseDetails/fetchByHome",
  async ({ home_id }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/house-details/${home_id}`);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      return { home_id, list: res.data?.data || [] };
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

// POST /api/house-details
export const createHouseDetailsThunk = createAsyncThunk(
  "houseDetails/create",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post("/api/house-details", payload);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      dispatch(fetchHouseDetailsThunk({ home_id: payload.home_id }));
      return true;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

// PUT /api/house-details/:fm_id
export const updateHouseDetailsThunk = createAsyncThunk(
  "houseDetails/update",
  async ({ fm_id, home_id, ...payload }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.put(`/api/house-details/${fm_id}`, payload);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      dispatch(fetchHouseDetailsThunk({ home_id }));
      return true;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

// DELETE /api/house-details/:fm_id
export const deleteHouseDetailsThunk = createAsyncThunk(
  "houseDetails/delete",
  async ({ fm_id, home_id }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.delete(`/api/house-details/${fm_id}`);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      dispatch(fetchHouseDetailsThunk({ home_id }));
      return true;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

const houseDetailsSlice = createSlice({
  name: "houseDetails",
  initialState: {
    home_id: null,
    list: [],
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearHouseDetailsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchHouseDetailsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHouseDetailsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.home_id = action.payload.home_id;
        state.list = action.payload.list || [];
      })
      .addCase(fetchHouseDetailsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load house details";
      })

      // create/update/delete -> saving
      .addMatcher(
        (a) =>
          a.type === createHouseDetailsThunk.pending.type ||
          a.type === updateHouseDetailsThunk.pending.type ||
          a.type === deleteHouseDetailsThunk.pending.type,
        (state) => {
          state.saving = true;
          state.error = null;
        }
      )
      .addMatcher(
        (a) =>
          a.type === createHouseDetailsThunk.fulfilled.type ||
          a.type === updateHouseDetailsThunk.fulfilled.type ||
          a.type === deleteHouseDetailsThunk.fulfilled.type,
        (state) => {
          state.saving = false;
        }
      )
      .addMatcher(
        (a) =>
          a.type === createHouseDetailsThunk.rejected.type ||
          a.type === updateHouseDetailsThunk.rejected.type ||
          a.type === deleteHouseDetailsThunk.rejected.type,
        (state, action) => {
          state.saving = false;
          state.error = action.payload || "Operation failed";
        }
      );
  },
});

export const { clearHouseDetailsError } = houseDetailsSlice.actions;
export default houseDetailsSlice.reducer;
